import React, { useState, useEffect } from 'react';
import { Bell, Search, User, LogOut, X } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../ui/Button';
import { useNavigate } from 'react-router-dom';
import { invoiceService } from '../../lib/database/invoices';
import { quoteService } from '../../lib/database/quotes';
import { clientService } from '../../lib/database/clients';
import { paymentService } from '../../lib/database/payments';
import { expenseService } from '../../lib/database/expenses';

interface SearchResult {
  id: string;
  type: 'invoice' | 'quote' | 'client' | 'payment' | 'expense';
  title: string;
  subtitle: string;
  amount?: number;
  status?: string;
}

export const Header: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  console.log('Header rendering - User:', user?.id, 'Role:', user?.role);

  // Debounced search function
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchTerm.trim().length >= 2) {
        performSearch(searchTerm.trim());
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const performSearch = async (query: string) => {
    if (!query || query.length < 2) return;

    setIsSearching(true);
    const results: SearchResult[] = [];

    try {
      // Search invoices
      const { data: invoices } = await invoiceService.getInvoices();
      if (invoices) {
        invoices
          .filter(invoice => 
            invoice.invoice_number?.toLowerCase().includes(query.toLowerCase()) ||
            invoice.client?.name?.toLowerCase().includes(query.toLowerCase()) ||
            invoice.client?.company?.toLowerCase().includes(query.toLowerCase())
          )
          .slice(0, 3)
          .forEach(invoice => {
            results.push({
              id: invoice.id,
              type: 'invoice',
              title: invoice.invoice_number,
              subtitle: `${invoice.client?.name || 'Unknown Client'} - ${invoice.client?.company || ''}`,
              amount: Number(invoice.total),
              status: invoice.status
            });
          });
      }

      // Search quotes
      const { data: quotes } = await quoteService.getQuotes();
      if (quotes) {
        quotes
          .filter(quote => 
            quote.quote_number?.toLowerCase().includes(query.toLowerCase()) ||
            quote.client?.name?.toLowerCase().includes(query.toLowerCase()) ||
            quote.client?.company?.toLowerCase().includes(query.toLowerCase())
          )
          .slice(0, 3)
          .forEach(quote => {
            results.push({
              id: quote.id,
              type: 'quote',
              title: quote.quote_number,
              subtitle: `${quote.client?.name || 'Unknown Client'} - ${quote.client?.company || ''}`,
              amount: Number(quote.total),
              status: quote.status
            });
          });
      }

      // Search clients
      const { data: clients } = await clientService.getClients();
      if (clients) {
        clients
          .filter(client => 
            client.name?.toLowerCase().includes(query.toLowerCase()) ||
            client.email?.toLowerCase().includes(query.toLowerCase()) ||
            client.company?.toLowerCase().includes(query.toLowerCase())
          )
          .slice(0, 3)
          .forEach(client => {
            results.push({
              id: client.id,
              type: 'client',
              title: client.name,
              subtitle: `${client.email} - ${client.company || 'No company'}`
            });
          });
      }

      // Search payments (admin only)
      if (user?.role === 'admin') {
        const { data: payments } = await paymentService.getPayments();
        if (payments) {
          payments
            .filter(payment => 
              payment.invoice?.invoice_number?.toLowerCase().includes(query.toLowerCase()) ||
              payment.invoice?.client?.name?.toLowerCase().includes(query.toLowerCase()) ||
              payment.reference_id?.toLowerCase().includes(query.toLowerCase())
            )
            .slice(0, 2)
            .forEach(payment => {
              results.push({
                id: payment.id,
                type: 'payment',
                title: `Payment #${payment.id.slice(-8)}`,
                subtitle: `${payment.invoice?.invoice_number || 'N/A'} - ${payment.invoice?.client?.name || 'Unknown'}`,
                amount: Number(payment.amount)
              });
            });
        }

        // Search expenses (admin only)
        const { data: expenses } = await expenseService.getExpenses();
        if (expenses) {
          expenses
            .filter(expense => 
              expense.vendor?.toLowerCase().includes(query.toLowerCase()) ||
              expense.category?.toLowerCase().includes(query.toLowerCase()) ||
              expense.notes?.toLowerCase().includes(query.toLowerCase())
            )
            .slice(0, 2)
            .forEach(expense => {
              results.push({
                id: expense.id,
                type: 'expense',
                title: `${expense.vendor} - ${expense.category}`,
                subtitle: expense.notes || 'No notes',
                amount: Number(expense.amount)
              });
            });
        }
      }

      setSearchResults(results);
      setShowResults(results.length > 0);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    setSearchTerm('');
    setShowResults(false);
    
    switch (result.type) {
      case 'invoice':
        navigate('/invoices');
        break;
      case 'quote':
        navigate('/quotes');
        break;
      case 'client':
        navigate('/clients');
        break;
      case 'payment':
        navigate('/payments');
        break;
      case 'expense':
        navigate('/expenses');
        break;
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
  };

  const getResultIcon = (type: string) => {
    switch (type) {
      case 'invoice':
        return '📄';
      case 'quote':
        return '💼';
      case 'client':
        return '👤';
      case 'payment':
        return '💳';
      case 'expense':
        return '📊';
      default:
        return '📋';
    }
  };

  const getStatusColor = (status?: string) => {
    if (!status) return '';
    switch (status) {
      case 'paid':
        return 'text-success-600';
      case 'overdue':
        return 'text-error-600';
      case 'sent':
        return 'text-warning-600';
      case 'approved':
        return 'text-success-600';
      case 'rejected':
        return 'text-error-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search invoices, clients, quotes, payments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowResults(true)}
              className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80 text-sm"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            
            {/* Search Results Dropdown */}
            {showResults && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                {isSearching ? (
                  <div className="p-4 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto mb-2"></div>
                    Searching...
                  </div>
                ) : searchResults.length > 0 ? (
                  <>
                    <div className="p-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-700">
                        Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    {searchResults.map((result) => (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleResultClick(result)}
                        className="w-full p-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors duration-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className="text-lg">{getResultIcon(result.type)}</span>
                            <div>
                              <p className="font-medium text-gray-900">{result.title}</p>
                              <p className="text-sm text-gray-600 truncate max-w-xs">{result.subtitle}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            {result.amount && (
                              <p className="font-semibold text-gray-900">
                                ₹{result.amount.toLocaleString()}
                              </p>
                            )}
                            {result.status && (
                              <p className={`text-xs font-medium capitalize ${getStatusColor(result.status)}`}>
                                {result.status}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </>
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    No results found for "{searchTerm}"
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Bolt.new Badge */}
          <a
            href="https://bolt.new"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center space-x-2 bg-gradient-to-r from-gray-900 to-black text-white px-4 py-2 rounded-full hover:from-black hover:to-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
              <span className="text-black font-bold text-sm">b</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-medium leading-none">POWERED BY</span>
              <span className="text-sm font-bold leading-none">BOLT.NEW</span>
            </div>
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          </a>

          <button className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200">
            <Bell size={20} />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-error-500 rounded-full"></span>
          </button>
          
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">{user?.full_name || 'User'}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.role || 'User'}</p>
            </div>
            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
              <User size={16} className="text-gray-600" />
            </div>
            <Button
              variant="ghost"
              size="sm"
              icon={LogOut}
              onClick={signOut}
              className="text-gray-500 hover:text-gray-700"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};